import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTasklistNavigateSegment, taskHeroPagePathFromTask } from '@/utils/tasklistPath';
import { normalizeTasklistJsonPayload } from '@/types/tasklistJson';
import { useSelectedStudent } from '@/context/SelectedStudentContext';
import { applyTasklistPlaceholders } from '@/utils/tasklistPagePlaceholders';

export interface Task {
  id: number;
  title: string;
  checklistName: string;
  completed: boolean;
  pathSegment?: string;
  /** `public/data/pages/{pageId}.json` — when set, opens `/task/page/{slug}`. */
  pageId?: string;
}

interface TaskCardProps {
  /** Optional live tasks from TasklistContext — merged with `tasklist.json` by `checklistName` / `id`. */
  tasks?: Task[];
  displayName?: string;
  role: 'LEARNING_COACH' | 'STUDENT';
  showTaskBadge?: boolean;
}

const CompleteIcon: React.FC = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden="true"
    data-testid="tcn-icon-complete"
    className="tcn-icon tcn-icon--complete"
  >
    <rect width="28" height="28" rx="14" fill="#01308E" />
    <path
      d="M19.25 9.625L11.8125 17.0625L8.75 14"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable={false}
    className="tcn-chevron"
  >
    <path
      d="M8.29289 5.29298C8.65901 4.92686 9.23801 4.90427 9.63078 5.22462L9.70696 5.29298L15.707 11.293C16.0975 11.6835 16.0975 12.3165 15.707 12.707L9.70696 18.707C9.31643 19.0976 8.68342 19.0976 8.29289 18.707C7.90237 18.3165 7.90237 17.6835 8.29289 17.293L13.5859 12L8.29289 6.70704L8.22453 6.63087C7.90418 6.2381 7.92678 5.65909 8.29289 5.29298Z"
      fill="currentColor"
    />
  </svg>
);

const SKELETON_COUNT = 3;

const TaskCard: React.FC<TaskCardProps> = ({
  tasks: liveTasks,
  displayName,
  role,
  showTaskBadge = true,
}) => {
  const navigate = useNavigate();
  const { selectedStudent } = useSelectedStudent();
  const placeholderVars = useMemo(
    () => ({
      studentName: selectedStudent?.nickName || selectedStudent?.firstName || 'your student',
      school: selectedStudent?.schoolShortName || selectedStudent?.schoolName || 'your school',
    }),
    [selectedStudent]
  );

  const [jsonTasks, setJsonTasks] = useState<Task[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetch('/data/tasklist.json')
      .then((r) => {
        if (!r.ok) throw new Error('tasklist.json');
        return r.json();
      })
      .then((data: unknown) => {
        const { tasks } = normalizeTasklistJsonPayload(data);
        setJsonTasks(
          tasks.map((t) => ({
            id: t.id,
            title: t.title,
            checklistName: t.checklistName,
            completed: Boolean(t.completed),
            pathSegment: t.pathSegment,
            pageId: t.pageId,
          }))
        );
      })
      .catch(() => setJsonTasks([]))
      .finally(() => setReady(true));
  }, []);

  const allTasks = useMemo(() => {
    /** Parent passed a filtered list (e.g. CMS `displayConditions`) — do not re-add rows from tasklist.json. */
    if (liveTasks != null) {
      return liveTasks.map((live) => {
        const j = jsonTasks.find(
          (t) => t.checklistName === live.checklistName || String(t.id) === String(live.id)
        );
        return {
          id: live.id,
          title: live.title,
          checklistName: live.checklistName,
          completed: live.completed,
          pageId: live.pageId ?? j?.pageId,
          pathSegment: live.pathSegment ?? j?.pathSegment,
        };
      });
    }
    if (!jsonTasks.length) {
      return [];
    }
    return jsonTasks;
  }, [jsonTasks, liveTasks]);

  const loading = !ready;

  const sortedTasks = [...allTasks].sort((a, b) => Number(a.completed) - Number(b.completed));
  const uncompletedCount = allTasks.filter((t) => !t.completed).length;

  const handleClick = (task: Task) => {
    const pid = task.pageId?.trim();
    if (pid) {
      navigate(taskHeroPagePathFromTask(task));
      return;
    }
    const segment = getTasklistNavigateSegment(task);
    if (!segment) return;
    navigate(`/tasklist/${segment}`);
  };

  return (
    <div className="task-card-new">
      <div className="tcn-header">
        <h3 className="tcn-heading">
          Your tasks{role === 'LEARNING_COACH' && displayName ? ` for ${displayName}` : ''}
        </h3>
        {showTaskBadge && !loading && uncompletedCount > 0 && (
          <span className="tcn-badge">{uncompletedCount}</span>
        )}
      </div>

      <ul className="tcn-list">
        {loading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <li className="tcn-item tcn-item--skeleton" key={i}>
                <div
                  className="placeholder placeholder-glow rounded-circle flex-shrink-0"
                  style={{ width: 28, height: 28 }}
                />
                <div
                  className="placeholder placeholder-glow rounded flex-grow-1"
                  style={{ height: 14 }}
                />
                <div
                  className="placeholder placeholder-glow rounded flex-shrink-0"
                  style={{ width: 16, height: 16 }}
                />
              </li>
            ))
          : sortedTasks.map((task) => {
              const displayTitle = applyTasklistPlaceholders(task.title, placeholderVars);
              return (
                <li
                  key={task.id}
                  title={displayTitle}
                  className={`tcn-item${task.completed ? ' tcn-item--completed' : ''}`}
                  onClick={() => handleClick(task)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleClick(task)}
                  aria-label={`${displayTitle}${task.completed ? ' (completed)' : ''}`}
                >
                  {task.completed ? (
                    <CompleteIcon />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="tcn-icon tcn-icon--incomplete"
                      data-testid="tcn-icon-incomplete"
                    />
                  )}

                  <span className="tcn-title">{displayTitle}</span>

                  <ChevronIcon />
                </li>
              );
            })}
      </ul>
    </div>
  );
};

export default TaskCard;
